import axios from 'axios';
import CoreHttpError from './errors/CoreHttpError';
import CoreSuccessWithError from './errors/CoreSuccessWithError';
import storage from '../store/storage';

export default class Repository {
	/**
	 * Base URL
	 */
	_baseUrl = process.env.VUE_APP_API_URL;

	/**
	 * Sends a GET HTTP request to the given endpoint with the given headers.
	 *
	 * @param {string} endpoint The HTTP endpoint.
	 * @param {object} options any additional axios options
	 * @param {object} headers Any additional headers
	 * @param {boolean} withToken (Optional) Send with bearer token?
	 * @returns {Promise<array|object|string>}
	 */
	get (endpoint, options = {}, headers = {}, withToken = false) {
		return this._request(endpoint, 'get', headers, {}, withToken, options);
	}

	/**
	 * Sends a POST HTTP request to the given endpoint with the given body and headers.
	 *
	 * @param {string} endpoint The HTTP endpoint.
	 * @param {object} body The JSON body to send in the POST request
	 * @param {object} headers Any additional headers
	 * @param {boolean} withToken (Optional) Send with bearer token?
	 * @returns {Promise<array|object|string>}
	 */
	post (endpoint, body = {}, headers = {}, withToken = false) {
		return this._request(endpoint, 'post', headers, body, withToken);
	}

	/**
	 * Sends a PUT HTTP request to the given endpoint with the given body and headers.
	 *
	 * @param {string} endpoint The HTTP endpoint.
	 * @param {object} body The JSON body to send in the PUT request
	 * @param {object} headers Any additional headers
	 * @param {boolean} withToken (Optional) Send with bearer token?
	 * @returns {Promise<array|object|string>}
	 */
	put (endpoint, body = {}, headers = {}, withToken = false) {
		return this._request(endpoint, 'put', headers, body, withToken);
	}

	/**
	 * Sends a PATCH HTTP request to the given endpoint with the given body and headers.
	 *
	 * @param {string} endpoint The HTTP endpoint.
	 * @param {object} body The JSON body to send in the PATCH request
	 * @param {object} headers Any additional headers
	 * @param {boolean} withToken (Optional) Send with bearer token?
	 * @returns {Promise<array|object|string>}
	 */
	patch (endpoint, body = {}, headers = {}, withToken = false) {
		return this._request(endpoint, 'patch', headers, body, withToken);
	}

	/**
	 * Sends a DELETE HTTP request to the given endpoint with the given headers.
	 *
	 * @param {string} endpoint The HTTP endpoint.
	 * @param {object} headers Any additional headers
	 * @param {boolean} withToken (Optional) Send with bearer token?
	 * @returns {Promise<array|object|string>}
	 */
	delete (endpoint, headers = {}, withToken = false) {
		return this._request(endpoint, 'delete', headers, undefined, withToken);
	}

	/**
	 * Sends a GET HTTP request to the given endpoint with the given token and headers.
	 *
	 * @param {string} endpoint The HTTP endpoint.
	 * @param {object} headers Any additional headers
	 * @param {object} options any additional axios options
	 * @returns {Promise<array|object|string>}
	 */
	getWithToken (endpoint, headers = {}, options = {}) {
		return this.get(endpoint, options, headers, true);
	}

	/**
	 * Sends a POST HTTP request to the given endpoint with the given body, token, and headers.
	 *
	 * @param {string} endpoint The HTTP endpoint.
	 * @param {object} body The JSON body to send in the POST request
	 * @param {object} headers Any additional headers
	 * @returns {Promise<array|object|string>}
	 */
	postWithToken (endpoint, body = {}, headers = {}) {
		return this.post(endpoint, body, headers, true);
	}

	/**
	 * Sends a PUT HTTP request to the given endpoint with the given body, token, and headers.
	 *
	 * @param {string} endpoint The HTTP endpoint.
	 * @param {object} body The JSON body to send in the PUT request
	 * @param {object} headers Any additional headers
	 * @returns {Promise<array|object|string>}
	 */
	putWithToken (endpoint, body = {}, headers = {}) {
		return this.put(endpoint, body, headers, true);
	}

	/**
	 * Sends a PATCH HTTP request to the given endpoint with the given body, token, and headers.
	 *
	 * @param {string} endpoint The HTTP endpoint.
	 * @param {object} body The JSON body to send in the PATCH request
	 * @param {object} headers Any additional headers
	 * @returns {Promise<array|object|string>}
	 */
	patchWithToken (endpoint, body = {}, headers = {}) {
		return this.patch(endpoint, body, headers, true);
	}

	/**
	 * Sends a DELETE HTTP request to the given endpoint with the given token and headers.
	 *
	 * @param {string} endpoint The HTTP endpoint.
	 * @param {object} headers Any additional headers
	 * @returns {Promise<array|object|string>}
	 */
	deleteWithToken (endpoint, headers = {}) {
		return this.delete(endpoint, headers, true);
	}

	/**
	 * Response data returned
	 */
	_returnData (response) {
		return response.data.data;
	}

	/**
	 * Success handler
	 *
	 * @param {Object} request Request info
	 * @param {Object} response Axios response
	 * @returns {Object} Returns response data or throws an error on error
	 *	responses
	 */
	_successHandler (request, response) {
		if (
			response?.data &&
			typeof response.data === 'object' &&
			'success' in response.data
		) {
			if (response.data.success) {
				return this._returnData(response);
			}
			else {
				throw new CoreSuccessWithError(request, response);
			}
		}
		else {
			return response.data;
		}
	}

	/**
	 * Error Handler
	 *
	 * @param {*} request API request info
	 * @param {*} error Axios error
	 */
	_errorHandler (request, error) {
		if (error instanceof CoreSuccessWithError) {
			throw error;
		}

		throw new CoreHttpError(request, error.response);
	}

	/**
	 * Send an HTTP API request to the core
	 *
	 * @param {string} url Relative URL
	 * @param {'get'|'post'|'put'|'patch'|'delete'} method HTTP Verb
	 * @param {Object} headers (Optional) Request headers
	 * @param {Object} payload Request payload
	 * @param {Object} withToken (Optional) Include bearer token with request?
	 * @param {Object} options Additional axios options
	 * @returns {Promise<any>} Returns a promise of the result
	 */
	async _request (url, method, headers = {}, payload = {}, withToken = false, options = {}) {
		if (withToken) {
			const token = storage.getToken();

			if (token) {
				headers.Authorization = `Bearer ${token}`;
			}
			else {
				console.warn('Attempting to make authenticated API request without a token!');
			}
		}

		const request = {
			method,
			url,
			headers,
			baseURL: this._baseUrl,
			...options
		};

		if (method === 'get') {
			request.params = payload;
		}
		else {
			request.data = payload;
		}

		return axios.request(request)
			.then(response => this._successHandler(request, response))
			.catch(response => this._errorHandler(request, response));
	}
}
